import { ClaimParentCard } from "@/components/parent/ClaimParentCard";
import { useEffect, useMemo, useRef, useState } from "react";
import { categoryFromBirth } from "@/lib/category";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CreditCard, Plus, Pencil, Users, ScanLine, Loader2, Upload, X, Info, ShoppingBag, Trash2, Settings, Shirt, Video, Eye, Clock, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { PlayerAvatar } from "@/components/shared/PlayerAvatar";
import futbuxCoin from "@/assets/futbux-coin.png";
import futbuxChest from "@/assets/futbux-chest.png";
import chuteiraOuroImg from "@/assets/item-chuteira-ouro.png";
import goldenBootsImg from "@/assets/item-golden-boots.png";
import goldenGlovesImg from "@/assets/item-golden-gloves.png";
import shinGuardsImg from "@/assets/item-shin-guards.png";
import { supabase } from "@/integrations/supabase/client";
import { listMyChildren, upsertMyChild } from "@/lib/api/parent-students.functions";
import { SkinEditor, DEFAULT_SKIN, type SkinValue } from "@/components/shared/SkinEditor";
import { getMyAthleteNumberConfig } from "@/lib/api/training-center.functions";
import {
  DEFAULT_ATHLETE_NUMBER_CONFIG,
  suggestAthleteNumbers,
  validateAthleteNumber,
  type AthleteNumberConfig,
} from "@/lib/athlete-number";
import { EditorErrorBoundary } from "@/components/shared/EditorErrorBoundary";
import { listMyStudentTCs, getSuggestedPlanForMyTc } from "@/lib/api/parent-invites.functions";
import { subscribeMyChildPlan } from "@/lib/api/parent-students.functions";
import { extractDocument } from "@/lib/api/document-ocr.functions";
import {
  listShopItems, upsertShopItem, deleteShopItem,
  listStudentInventory, purchaseShopItem,
  setInventoryEquipped,
} from "@/lib/api/shop.functions";
import { cropPhotoFromDocument } from "@/lib/document-photo";
import { rotateImageFromUrl } from "@/lib/image-rotate";
import { cn } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";




export const Route = createFileRoute("/_authenticated/responsavel/alunos/")({
  head: () => ({
    meta: [
      { title: "Meus Alunos — FutBux" },
      { name: "description", content: "Área do responsável para cadastrar e acompanhar os alunos vinculados ao centro de treinamento." },
      { property: "og:title", content: "Meus Alunos — FutBux" },
      { property: "og:description", content: "Cadastre e acompanhe os alunos vinculados ao centro de treinamento pelo portal do responsável." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ParentStudentsPage,
});

const ACTIVE_KEY = "futbux:activeParentTc";

// ---- helpers ----
function maskCpf(v: string) {
  return v.replace(/\D/g, "").slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

const emptyForm = () => ({
  id: null as string | null,
  name: "",
  cpf: "",
  birth_date: "",
  category: "",
  gender: "masculino",
  is_goalkeeper: false,
  photo_url: null as string | null,
  weight: "",
  height: "",
  jersey_number: "",
  jersey_name: "",
  youtube_url: "",
  tournaments: [] as Array<{ name: string; year?: string; result?: string }>,
  has_health_plan: false,
  health_plan_name: "",
  health_plan_contact: "",
  document_type: "",
  document_front_url: null as string | null,
  document_back_url: null as string | null,
  avatar_config: null as any,
});

function ParentStudentsPage() {
  const navigate = useNavigate();
  const listTcs = useServerFn(listMyStudentTCs);
  const listFn = useServerFn(listMyChildren);
  const upsert = useServerFn(upsertMyChild);
  const numberConfigFn = useServerFn(getMyAthleteNumberConfig);
  const [numberConfig, setNumberConfig] = useState<AthleteNumberConfig>(DEFAULT_ATHLETE_NUMBER_CONFIG);
  const [usedNumbers, setUsedNumbers] = useState<string[]>([]);
  useEffect(() => {
    let alive = true;
    numberConfigFn({} as any)
      .then((c: any) => {
        if (!alive || !c) return;
        setNumberConfig({ digits: c.digits, allowRepeated: c.allowRepeated });
        setUsedNumbers(Array.isArray(c.used) ? c.used : []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const [tcId, setTcId] = useState<string | null>(null);
  const [tcName, setTcName] = useState<string>("");
  const [students, setStudents] = useState<any[]>([]);
  const [parentInfo, setParentInfo] = useState<{ id: string; name: string | null; phone: string | null; email: string | null; cpf?: string | null; birth_date?: string | null } | null>(null);
  const [sameAsParent, setSameAsParent] = useState(false);

  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [numberAlert, setNumberAlert] = useState<string | null>(null);

  const [tab, setTab] = useState<"aluno" | "resp" | "curr" | "skin">("aluno");
  // Quando o modal é aberto pelo botão "Editar Skin", só a aba Skin fica visível.
  const [skinOnly, setSkinOnly] = useState(false);
  const [showOcrIntro, setShowOcrIntro] = useState(false);
  const suggestedPlanFn = useServerFn(getSuggestedPlanForMyTc);
  const subscribePlanFn = useServerFn(subscribeMyChildPlan);
  const [suggestedPlan, setSuggestedPlan] = useState<any | null>(null);
  const [planPrompt, setPlanPrompt] = useState<{ studentId: string; studentName: string } | null>(null);
  const [planBusy, setPlanBusy] = useState(false);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = (() => { try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; } })();
        const tcsRes = await listTcs();
        if (cancelled) return;
        const tcs = tcsRes.tcs ?? [];
        if (tcs.length === 0) return;
        const active = tcs.find((t: any) => t.training_center_id === stored) ?? tcs[0];
        if (tcs.length > 1 && !stored) {
          navigate({ to: "/selecionar-ct" });
          return;
        }
        setTcId(active.training_center_id);
        setTcName(active.tc_name);
        try { localStorage.setItem(ACTIVE_KEY, active.training_center_id); } catch {}
        const res = await listFn({ data: { tcId: active.training_center_id } });
        if (cancelled) return;
        setStudents(res.students ?? []);
        setParentInfo(res.parent ?? null);
        suggestedPlanFn({ data: { tcId: active.training_center_id } })
          .then((r: any) => { if (!cancelled) setSuggestedPlan(r?.plan ?? null); })
          .catch(() => {});
      } catch (err: any) {
        console.error(err);
        if (!cancelled) toast.error(err?.message || "Erro ao carregar seus alunos.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    if (!tcId) return;
    const res = await listFn({ data: { tcId } });
    setStudents(res.students ?? []);
    setParentInfo(res.parent ?? null);
  };


  const openNew = () => {
    setForm(emptyForm());
    setSaveError(null);
    setTab("aluno");
    setSkinOnly(false);
    setShowOcrIntro(true);
    setSameAsParent(false);
    setOpen(true);
  };


  const openEdit = (s: any, initialTab: "aluno" | "resp" | "curr" | "skin" = "aluno") => {
    setSaveError(null);
    setForm({
      id: s.id,
      name: s.name || "",
      cpf: s.cpf ? maskCpf(s.cpf) : "",
      birth_date: s.birth_date || "",
      category: s.category || "",
      gender: s.gender || "masculino",
      is_goalkeeper: !!s.is_goalkeeper,
      photo_url: s.photo_url || null,
      weight: s.weight != null ? String(s.weight) : "",
      height: s.height != null ? String(s.height) : "",
      jersey_number: s.jersey_number || "",
      jersey_name: s.jersey_name || "",
      youtube_url: s.youtube_url || "",
      tournaments: Array.isArray(s.tournaments) ? s.tournaments : [],
      has_health_plan: !!s.has_health_plan,
      health_plan_name: s.health_plan_name || "",
      health_plan_contact: s.health_plan_contact || "",
      document_type: s.document_type || "",
      document_front_url: s.document_front_url || null,
      document_back_url: s.document_back_url || null,
      avatar_config: s.avatar_config ?? null,
    });
    setTab(initialTab);
    setSkinOnly(initialTab === "skin");
    setShowOcrIntro(false);
    setSameAsParent(false);
    setOpen(true);
  };


  const setF = (patch: Partial<ReturnType<typeof emptyForm>>) => setForm((f) => ({ ...f, ...patch }));

  // auto category from birth
  useEffect(() => {
    if (form.birth_date) {
      const c = categoryFromBirth(form.birth_date);
      if (c && c !== form.category) setF({ category: c });
    }
  }, [form.birth_date]);

  const numberCheck = validateAthleteNumber(form.jersey_number, numberConfig.digits);
  const numberValidation = useMemo(() => {
    if (!form.jersey_number) return { status: "idle" as const };
    if (numberCheck.error) return { status: "error" as const };
    if (
      !numberConfig.allowRepeated &&
      usedNumbers.filter((u) => u !== (form.jersey_number ?? "")).includes(numberCheck.value ?? "")
    ) {
      return { status: "error" as const };
    }
    return { status: "ok" as const };
  }, [form.jersey_number, numberConfig, usedNumbers, numberCheck.error, numberCheck.value]);
  const numberSuggestions = useMemo(
    () =>
      numberConfig.allowRepeated
        ? []
        : suggestAthleteNumbers({
            query: form.jersey_number,
            digits: numberConfig.digits,
            used: usedNumbers.filter((u) => u !== (form.jersey_number ?? "")),
            limit: 12,
          }),
    [form.jersey_number, numberConfig, usedNumbers],
  );

  const save = async () => {
    if (!tcId) return;
    if (!form.name.trim()) { toast.error("Nome do aluno é obrigatório."); setTab("aluno"); return; }
    if (numberCheck.error) { toast.error(numberCheck.error); setTab("skin"); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const isNew = !form.id;
      const saved: any = await upsert({
        data: {
          id: form.id ?? undefined,
          tcId,
          name: form.name.trim(),
          cpf: form.cpf.replace(/\D/g, "") || null,
          birth_date: form.birth_date || null,
          category: form.category || null,
          gender: form.gender || null,
          is_goalkeeper: form.is_goalkeeper,
          photo_url: form.photo_url,
          weight: form.weight || null,
          height: form.height || null,
          jersey_number: form.jersey_number || null,
          jersey_name: form.jersey_name || null,
          youtube_url: form.youtube_url || null,
          tournaments: form.tournaments,
          has_health_plan: form.has_health_plan,
          health_plan_name: form.health_plan_name || null,
          health_plan_contact: form.health_plan_contact || null,
          document_type: form.document_type || null,
          document_front_url: form.document_front_url,
          document_back_url: form.document_back_url,
          avatar_config: form.avatar_config,
        },
      });
      toast.success("Aluno salvo");
      setOpen(false);
      await reload();
      if (isNew && saved?.id) {
        setPlanPrompt({ studentId: saved.id, studentName: form.name.trim() });
      }
    } catch (e: any) {
      const message = e?.message || "Não foi possível salvar. Tente novamente.";
      setSaveError(message);
      if (/já está em uso/i.test(message)) {
        setTab("skin");
        setNumberAlert(message);
      } else {
        toast.error(message);
      }

    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-muted-foreground">Carregando…</div>;
  if (!tcId) return <ClaimParentCard />;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Meus Alunos</h1>
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" />{tcName}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" /> Cadastrar aluno
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={() => navigate({ to: "/responsavel/planos" })}
          >
            <CreditCard className="w-4 h-4 mr-2" /> Planos de pagamento
          </Button>
        </div>
      </div>

      {students.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-muted-foreground">Nenhum aluno cadastrado ainda.</CardContent></Card>
      ) : (
        <div className="flex flex-col gap-6">
          {students.map((s) => (
            <StudentBigCard
              key={s.id}
              student={s}
              tcId={tcId}
              parentName={parentInfo?.name ?? ""}
              onEdit={() => openEdit(s)}
              onEditSkin={() => openEdit(s, "skin")}
              onEditVideo={() => openEdit(s, "curr")}
              onOpen={() => navigate({ to: "/responsavel/alunos/$id", params: { id: s.id } })}
              onBalanceChanged={reload}
            />
          ))}
        </div>
      )}


      {/* Plano de pagamento após o cadastro */}
      <Dialog open={!!planPrompt} onOpenChange={(v) => { if (!v) setPlanPrompt(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" /> Atribua um plano de pagamento
            </DialogTitle>
            <DialogDescription>
              O cadastro de <strong>{planPrompt?.studentName}</strong> foi enviado ao {tcName} e ficará
              <strong> inativo até a confirmação</strong> do Centro de Treinamento. É necessário atribuir
              um plano de pagamento ao aluno.
            </DialogDescription>
          </DialogHeader>

          {suggestedPlan ? (
            <div className="rounded-lg border p-3 space-y-1 bg-muted/30">
              <p className="text-xs text-muted-foreground">Plano sugerido pelo Centro de Treinamento</p>
              <p className="font-semibold">{suggestedPlan.plan_type}</p>
              <p className="text-sm">
                {Number(suggestedPlan.price ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                {suggestedPlan.validity ? ` • ${suggestedPlan.validity}` : ""}
              </p>
            </div>
          ) : null}

          <DialogFooter className="flex-col sm:flex-col gap-2">
            {suggestedPlan ? (
              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                disabled={planBusy}
                onClick={async () => {
                  if (!tcId || !planPrompt) return;
                  setPlanBusy(true);
                  try {
                    const methods: string[] = Array.isArray(suggestedPlan.accepted_payment_methods)
                      ? suggestedPlan.accepted_payment_methods
                      : [];
                    await subscribePlanFn({
                      data: {
                        tcId,
                        studentId: planPrompt.studentId,
                        planPriceId: suggestedPlan.id,
                        paymentMethod: methods[0] ?? "pix",
                      },
                    });
                    toast.success("Plano confirmado!");
                    setPlanPrompt(null);
                    await reload();
                  } catch (e: any) {
                    toast.error(e?.message || "Não foi possível confirmar o plano.");
                  } finally {
                    setPlanBusy(false);
                  }
                }}
              >
                {planBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirmar plano sugerido
              </Button>
            ) : null}
            <Button
              variant={suggestedPlan ? "outline" : "default"}
              className="w-full"
              onClick={() => { setPlanPrompt(null); navigate({ to: "/responsavel/planos" }); }}
            >
              Ver planos de pagamento
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setPlanPrompt(null)}>
              Depois
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OCR intro modal */}
      <Dialog open={showOcrIntro} onOpenChange={setShowOcrIntro}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Info className="w-5 h-5 text-primary" /> Como quer preencher?</DialogTitle>
            <DialogDescription>
              Preenchendo pela foto da identidade o cadastro fica mais completo e sem erros de digitação.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Button
              className="bg-green-600 hover:bg-green-700 text-white justify-start"
              onClick={() => { setShowOcrIntro(false); setTab("curr"); }}
            >
              <ScanLine className="w-4 h-4 mr-2" />
              Tirar foto do documento (recomendado)
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => { setShowOcrIntro(false); setTab("aluno"); }}>
              Prosseguir manualmente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Student form modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{skinOnly ? "Editar skin" : form.id ? "Editar aluno" : "Novo aluno"}</DialogTitle>
            <DialogDescription>
              {skinOnly
                ? "Personalize a aparência do seu atleta."
                : <>Comece capturando a foto do documento na aba <b>Aluno</b> para preencher tudo automaticamente.</>}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
            <TabsList className={skinOnly ? "grid grid-cols-1 w-full" : "grid grid-cols-4 w-full"}>
              {!skinOnly && <TabsTrigger value="aluno">Aluno</TabsTrigger>}
              {!skinOnly && <TabsTrigger value="resp">Responsável</TabsTrigger>}
              {!skinOnly && <TabsTrigger value="curr">Currículo</TabsTrigger>}
              <TabsTrigger value="skin">Skin</TabsTrigger>
            </TabsList>


            {/* ===== ALUNO ===== */}
            <TabsContent value="aluno" className="space-y-3 pt-3">
              <div className="rounded-lg border p-3 space-y-3 bg-green-50/40 border-green-200">
                <div className="flex items-center gap-2">
                  <ScanLine className="w-4 h-4 text-green-700" />
                  <h4 className="font-medium text-green-800">Captura inteligente por documento</h4>
                </div>
                <p className="text-xs text-muted-foreground">
                  Envie a frente do RG ou CNH e o sistema preenche nome, CPF e nascimento.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <DocPhoto tcId={tcId} value={form.document_front_url} onChange={(p) => setF({ document_front_url: p })} label="Frente do documento" />
                  <DocPhoto tcId={tcId} value={form.document_back_url} onChange={(p) => setF({ document_back_url: p })} label="Verso (opcional)" />
                </div>
                <OcrButton
                  paths={[form.document_front_url, form.document_back_url].filter(Boolean) as string[]}
                  onResult={async (r) => {
                    setF({
                      name: r.name ?? form.name,
                      cpf: r.cpf ? maskCpf(r.cpf) : form.cpf,
                      birth_date: r.birth_date ?? form.birth_date,
                      document_type: form.document_type || "rg",
                    });
                    setTab("aluno");
                    toast.success(
                      r.cpf
                        ? "Dados extraídos! Revise antes de salvar."
                        : "Dados extraídos, mas o CPF não foi identificado — preencha manualmente.",
                    );

                    // Usa a foto do documento como foto do atleta
                    const paths = [form.document_front_url, form.document_back_url].filter(Boolean) as string[];
                    const docPath = r.photo_path_index != null ? paths[r.photo_path_index] : null;
                    if (r.photo_box && docPath && !form.photo_url) {
                      const file = await cropPhotoFromDocument(docPath, r.photo_box);
                      if (file) {
                        try {
                          const { data: userData } = await supabase.auth.getUser();
                          const uid = userData.user?.id;
                          if (uid) {
                            const path = `${tcId}/u/${uid}/${crypto.randomUUID()}.jpg`;
                            const { error } = await supabase.storage
                              .from("student_photos")
                              .upload(path, file, { contentType: "image/jpeg" });
                            if (!error) {
                              setF({ photo_url: path });
                              toast.success("Foto do documento aplicada como foto do atleta.");
                            }
                          }
                        } catch { /* silencioso */ }
                      }
                    }
                  }}
                />

              </div>

              {!form.id && parentInfo && (
                <label className="flex items-start gap-3 rounded-lg border-2 border-primary/40 bg-primary/5 p-3 cursor-pointer">
                  <Checkbox
                    checked={sameAsParent}
                    onCheckedChange={(v) => {
                      const checked = v === true;
                      setSameAsParent(checked);
                      if (checked) {
                        setF({
                          name: parentInfo.name || form.name,
                          cpf: parentInfo.cpf ? maskCpf(parentInfo.cpf) : form.cpf,
                          birth_date: parentInfo.birth_date || form.birth_date,
                        });
                        toast.success("Dados do responsável aplicados ao aluno.");
                      }
                    }}
                  />
                  <span className="text-sm">
                    <span className="font-medium">Sou o próprio aluno</span>
                    <span className="block text-xs text-muted-foreground">
                      O responsável é a mesma pessoa que o aluno — puxa nome, CPF e data de nascimento já cadastrados.
                    </span>
                  </span>
                </label>
              )}

              <StudentPhoto tcId={tcId} value={form.photo_url} onChange={(p) => setF({ photo_url: p })} label="Foto do rosto" />

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1 sm:col-span-2">
                  <Label>Nome completo *</Label>
                  <Input value={form.name} onChange={(e) => setF({ name: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>CPF</Label>
                  <Input value={form.cpf} onChange={(e) => setF({ cpf: maskCpf(e.target.value) })} placeholder="000.000.000-00" inputMode="numeric" />
                </div>
                <div className="space-y-1">
                  <Label>Data de nascimento</Label>
                  <Input type="date" value={form.birth_date} onChange={(e) => setF({ birth_date: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Categoria</Label>
                  <Input value={form.category} onChange={(e) => setF({ category: e.target.value })} placeholder="Sub-12" />
                  <p className="text-xs text-muted-foreground">Preenchida automaticamente pela data de nascimento.</p>
                </div>
                <div className="space-y-1">
                  <Label>Sexo</Label>
                  <select
                    className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 text-sm"
                    value={form.gender}
                    onChange={(e) => setF({ gender: e.target.value })}
                  >
                    <option value="">Selecione</option>
                    <option value="masculino">Masculino</option>
                    <option value="feminino">Feminino</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Peso (kg)</Label>
                  <Input type="number" step="0.1" value={form.weight} onChange={(e) => setF({ weight: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Altura (m)</Label>
                  <Input type="number" step="0.01" value={form.height} onChange={(e) => setF({ height: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Switch checked={form.is_goalkeeper} onCheckedChange={(v) => setF({ is_goalkeeper: v })} />
                <Label>Goleiro</Label>
              </div>

              <div className="space-y-3 border-t pt-3">
                <div className="flex items-center gap-2">
                  <Switch checked={form.has_health_plan} onCheckedChange={(v) => setF({ has_health_plan: v })} />
                  <Label>Plano de saúde</Label>
                </div>
                {form.has_health_plan && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Nome do plano</Label>
                      <Input value={form.health_plan_name} onChange={(e) => setF({ health_plan_name: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label>Contato</Label>
                      <Input value={form.health_plan_contact} onChange={(e) => setF({ health_plan_contact: e.target.value })} />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>


            {/* ===== RESPONSÁVEL ===== */}
            <TabsContent value="resp" className="space-y-3 pt-3">
              <p className="text-sm text-muted-foreground">
                Os dados do responsável são gerenciados na sua conta.
                Para atualizar telefone, e-mail ou endereço, acesse <b>Meu Perfil</b>.
              </p>
              <Button variant="outline" onClick={() => navigate({ to: "/responsavel/perfil" as any })}>
                Abrir Meu Perfil
              </Button>
            </TabsContent>

            {/* ===== CURRÍCULO ===== */}
            <TabsContent value="curr" className="space-y-4 pt-3">
              <div className="space-y-1">
                <Label>Vídeo do YouTube (opcional)</Label>
                <Input value={form.youtube_url} onChange={(e) => setF({ youtube_url: e.target.value })} placeholder="https://youtube.com/..." />
              </div>

              <div className="space-y-2">
                <Label>Torneios e conquistas</Label>
                {form.tournaments.map((t, i) => (
                  <div key={i} className="grid grid-cols-[1fr_100px_1fr_auto] gap-2 items-center">
                    <Input value={t.name} placeholder="Nome do torneio" onChange={(e) => {
                      const arr = [...form.tournaments]; arr[i] = { ...arr[i], name: e.target.value }; setF({ tournaments: arr });
                    }} />
                    <Input value={t.year ?? ""} placeholder="Ano" onChange={(e) => {
                      const arr = [...form.tournaments]; arr[i] = { ...arr[i], year: e.target.value }; setF({ tournaments: arr });
                    }} />
                    <Input value={t.result ?? ""} placeholder="Resultado" onChange={(e) => {
                      const arr = [...form.tournaments]; arr[i] = { ...arr[i], result: e.target.value }; setF({ tournaments: arr });
                    }} />
                    <Button size="icon" variant="ghost" onClick={() => setF({ tournaments: form.tournaments.filter((_, j) => j !== i) })}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => setF({ tournaments: [...form.tournaments, { name: "" }] })}>
                  <Plus className="w-4 h-4 mr-1" /> Adicionar torneio
                </Button>
              </div>
            </TabsContent>


            {/* ===== SKIN ===== */}
            <TabsContent value="skin" className="space-y-3 pt-3">
              <div className="rounded-lg border p-3 space-y-2">
                <Label className="text-xs">Número do Atleta (até {numberConfig.digits} algarismos)</Label>
                <Input
                  value={form.jersey_number}
                  onChange={(e) => setF({ jersey_number: e.target.value.replace(/\D/g, "").slice(0, numberConfig.digits) })}
                  placeholder="10"
                  inputMode="numeric"
                  className={cn(
                    numberValidation.status === "error" && "border-destructive text-destructive focus-visible:ring-destructive",
                    numberValidation.status === "ok" && "border-green-500 text-green-700 focus-visible:ring-green-500",
                  )}
                />
                {numberCheck.error ? (
                  <p role="alert" className="text-xs text-destructive">{numberCheck.error}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Este número é único do atleta e vale também no Centro de Treinamento, no Uniforme e na Skin.
                  </p>
                )}
                {numberSuggestions.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {numberSuggestions.map((n) => (
                      <Button
                        key={n}
                        type="button"
                        size="sm"
                        variant={form.jersey_number === n ? "default" : "outline"}
                        className="h-7 px-2 text-xs"
                        onClick={() => setF({ jersey_number: n, avatar_config: { ...((form.avatar_config as any) ?? DEFAULT_SKIN), shirtNumber: n } })}
                      >
                        {n}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              <EditorErrorBoundary resetKey={`${form.id ?? "new"}:${tab}`}>
                <SkinEditor
                  value={(form.avatar_config as SkinValue) ?? DEFAULT_SKIN}
                  onChange={(v: SkinValue) => setF({ avatar_config: v })}
                  jerseyNumber={form.jersey_number}
                  jerseyName={form.jersey_name}
                  onJerseyNumberChange={(v: string) => setF({ jersey_number: v.replace(/\D/g, "").slice(0, numberConfig.digits) })}
                  onJerseyNameChange={(v: string) => setF({ jersey_name: v.slice(0, 20) })}
                  hint="Personalize a aparência do seu atleta. O Centro de Treinamento também pode ajustar."
                />
              </EditorErrorBoundary>

            </TabsContent>
          </Tabs>

          <DialogFooter>
            {saveError ? <p role="alert" className="mr-auto text-sm text-destructive">{saveError}</p> : null}
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!numberAlert} onOpenChange={(o) => !o && setNumberAlert(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Número já utilizado</AlertDialogTitle>
            <AlertDialogDescription>
              {numberAlert} Escolha outro número para o atleta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setNumberAlert(null)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>

  );
}

// ============ Photo uploads (mirrors CT modal, uses tcId folder) ============

function useSignedUrl(path: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!path) { setUrl(null); return; }
      const { data } = await supabase.storage.from("student_photos").createSignedUrl(path, 3600);
      if (!cancel) setUrl(data?.signedUrl ?? null);
    })();
    return () => { cancel = true; };
  }, [path]);
  return url;
}

function StudentBigCard({
  student, tcId, parentName, onEdit, onEditSkin, onEditVideo, onOpen, onBalanceChanged,
}: {
  student: any; tcId: string; parentName: string;
  onEdit: () => void; onEditSkin: () => void; onEditVideo: () => void;
  onOpen: () => void; onBalanceChanged: () => void;
}) {
  const navigate = useNavigate();
  const photo = useSignedUrl(student.photo_url ?? null);
  const awaitingTc =
    (student.created_via ?? "ct") === "link" && !student.enrollment_confirmed_at;
  const missingPlan = !student.plan_price_id;
  const missingSchedule = !student.has_schedule;
  const canSelfEnroll = student.plan_prices?.allow_self_enroll === true;
  const isActive =
    !awaitingTc && ((student.status ?? "active") === "active" || student.status === "ativo");
  const payment = (student.payment_status ?? "pendente") as string;
  const paymentColors: Record<string, string> = {
    pago: "bg-emerald-500 text-white",
    pendente: "bg-amber-500 text-white",
    atrasado: "bg-red-500 text-white",
  };
  const paymentLabel = payment.charAt(0).toUpperCase() + payment.slice(1);
  const futbuxAmount = Number(student.futbux_balance ?? 0);

  const [storeOpen, setStoreOpen] = useState(false);
  const [inventory, setInventory] = useState<any[]>([]);
  const loadInventory = useServerFn(listStudentInventory);
  const equipFn = useServerFn(setInventoryEquipped);
  const [busyInvId, setBusyInvId] = useState<string | null>(null);

  const reloadInventory = async () => {
    try {
      const r = await loadInventory({ data: { studentId: student.id } });
      setInventory(r.items ?? []);
    } catch {}
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await loadInventory({ data: { studentId: student.id } });
        if (!cancel) setInventory(r.items ?? []);
      } catch {}
    })();
    return () => { cancel = true; };
  }, [student.id, student.futbux_balance]);

  const equipped = useMemo(() => {
    const eq: { boots?: boolean; gloves?: boolean; shinguards?: boolean } = {};
    for (const inv of inventory) {
      if (!inv.equipped) continue;
      const slot = inv.shop_items?.slot as string | null;
      if (slot === "boots" || slot === "gloves" || slot === "shinguards") {
        (eq as any)[slot] = true;
      }
    }
    return eq;
  }, [inventory]);

  const toggleEquip = async (inv: any) => {
    setBusyInvId(inv.id);
    try {
      await equipFn({ data: { studentId: student.id, inventoryId: inv.id, equipped: !inv.equipped } });
      await reloadInventory();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao equipar");
    } finally { setBusyInvId(null); }
  };

  return (
    <Card className="overflow-hidden rounded-2xl border-2 border-primary/40">
      <CardContent className="p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(220px,1fr)_minmax(280px,1.4fr)_minmax(220px,1fr)] gap-4 md:gap-6">
          {/* LEFT: Skin/Avatar + ações de skin/vídeo */}
          <div className="flex flex-col gap-2">
            <div className="rounded-2xl border-2 border-primary/40 bg-muted/20 flex items-center justify-center p-3 min-h-[320px] flex-1">
              <PlayerAvatar config={student.avatar_config} equipped={equipped} className="h-full w-full max-h-[380px]" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" onClick={onEditSkin} className="bg-primary text-primary-foreground">
                <Shirt className="w-4 h-4 mr-1" /> Editar Skin
              </Button>
              <Button size="sm" variant="outline" onClick={onEditVideo}>
                <Video className="w-4 h-4 mr-1" /> {student.youtube_url ? "Trocar vídeo" : "Colocar vídeo"}
              </Button>
            </div>
          </div>


          {/* MIDDLE: FUTBUX + Inventory */}
          <div className="flex flex-col gap-4">
            {/* FUTBUX box */}
            <div className="rounded-2xl border-2 border-primary/40 p-3 flex items-center justify-around gap-3">
              <div className="flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-100 to-yellow-50 border border-amber-300 px-3 py-1.5">
                <img src={futbuxCoin} alt="FUTBUX" width={40} height={40} loading="lazy" className="w-10 h-10" />
                <span className="text-xl font-bold text-amber-900 tabular-nums">
                  {futbuxAmount.toLocaleString("pt-BR")}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <img src={futbuxChest} alt="Baú FUTBUX" width={72} height={72} loading="lazy" className="w-16 h-16 object-contain" />
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mt-1">FUTBUX</span>
              </div>
            </div>

            {/* Inventário box */}
            <div className="rounded-2xl border-2 border-primary/40 p-3 flex-1 min-h-[220px]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold">Inventário</h3>
                <Button size="sm" onClick={() => setStoreOpen(true)} className="bg-sky-500 hover:bg-sky-600 text-white">
                  <ShoppingBag className="w-4 h-4 mr-1" /> Obter Itens
                </Button>
              </div>
              {inventory.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">Nenhum item ainda. Use seus FUTBUX na loja!</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {inventory.map((inv) => {
                    const slot = inv.shop_items?.slot as string | null;
                    const canEquip = slot === "boots" || slot === "gloves" || slot === "shinguards";
                    return (
                      <div key={inv.id} className={`flex flex-col items-center gap-1 p-2 rounded-lg border ${inv.equipped ? "border-emerald-500 bg-emerald-500/10" : "border-transparent bg-muted/30"}`}>
                        <div className="w-16 h-16 rounded-lg bg-muted/40 flex items-center justify-center overflow-hidden">
                          <ItemImage url={inv.shop_items?.image_url} name={inv.shop_items?.name} />
                        </div>
                        <span className="text-[10px] text-center leading-tight font-medium truncate w-full">
                          {inv.shop_items?.name ?? "Item"}
                        </span>
                        <div className="flex items-center gap-1 text-[10px] font-bold text-amber-600">
                          <img src={futbuxCoin} alt="" className="w-3 h-3" />
                          {Number(inv.cost_paid || 0).toLocaleString("pt-BR")}
                        </div>
                        <div className="flex gap-1 w-full flex-wrap">
                          {canEquip && !inv.equipped && (
                            <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] flex-1" disabled={busyInvId === inv.id} onClick={() => toggleEquip(inv)}>
                              Equipar
                            </Button>
                          )}
                          {canEquip && inv.equipped && (
                            <Button size="sm" variant="default" className="h-6 px-1.5 text-[10px] flex-1" disabled={busyInvId === inv.id} onClick={() => toggleEquip(inv)}>
                              Desequipar
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

              )}
            </div>
          </div>

          {/* RIGHT: photo + info + actions */}
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl border-2 border-primary/40 bg-muted/30 min-h-[220px] flex items-center justify-center overflow-hidden">
              {photo ? (
                <img src={photo} alt={student.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-8xl font-bold text-muted-foreground">
                  {student.name?.charAt(0)?.toUpperCase() ?? "?"}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="font-bold text-2xl truncate">{student.name}</span>
              <span className="text-xs text-muted-foreground truncate">
                <span className="font-medium text-foreground">Responsável:</span> {parentName || "—"}
              </span>
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {student.category || "Sem categoria"}
              {student.gender ? ` · ${student.gender}` : ""}
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge className={`${isActive ? "bg-blue-600 text-white" : "bg-gray-500 text-white"}`}>
                {isActive ? "Ativo" : awaitingTc ? "Inativo • aguardando confirmação do CT" : "Inativo"}
              </Badge>
              <Badge className={paymentColors[payment] ?? paymentColors.pendente}>
                {paymentLabel}
              </Badge>
            </div>

            {(missingPlan || missingSchedule || awaitingTc) && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 space-y-1.5">
                {awaitingTc && (
                  <p className="text-xs text-amber-800 flex items-center gap-1">
                    <Clock className="w-3 h-3 shrink-0" /> Aguardando confirmação do centro de treinamento.
                  </p>
                )}
                {missingPlan && (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-amber-800">Falta escolher plano de pagamento.</p>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] shrink-0" onClick={() => navigate({ to: "/responsavel/planos" })}>
                      Escolher plano
                    </Button>
                  </div>
                )}
                {missingSchedule && (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-amber-800">Falta escolher turma.</p>
                    {canSelfEnroll ? (
                      <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] shrink-0" onClick={() => navigate({ to: "/responsavel/treinamento" })}>
                        Escolher turma
                      </Button>
                    ) : (
                      <span className="text-[10px] text-amber-700 shrink-0">Aguarde o CT alocar</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 mt-auto">
              <Button size="sm" variant="ghost" onClick={onOpen}>
                <Eye className="w-3 h-3 mr-1" /> Ver
              </Button>
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Pencil className="w-3 h-3 mr-1" /> Editar dados
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      <StoreDialog
        open={storeOpen}
        onOpenChange={setStoreOpen}
        tcId={tcId}
        studentId={student.id}
        studentName={student.name}
        balance={futbuxAmount}
        onPurchased={async () => {
          const r = await loadInventory({ data: { studentId: student.id } });
          setInventory(r.items ?? []);
          onBalanceChanged();
        }}
      />
    </Card>
  );
}

function ItemImage({ url, name }: { url?: string | null; name?: string | null }) {
  const lower = (name || "").toLowerCase();
  if (!url) {
    if (lower.includes("luva")) return <img src={goldenGlovesImg} alt={name ?? "Item"} className="w-full h-full object-contain" loading="lazy" />;
    if (lower.includes("caneleira")) return <img src={shinGuardsImg} alt={name ?? "Item"} className="w-full h-full object-contain" loading="lazy" />;
    if (lower.includes("chuteira de ouro")) return <img src={goldenBootsImg} alt={name ?? "Item"} className="w-full h-full object-contain" loading="lazy" />;
    if (lower.includes("chuteira")) return <img src={chuteiraOuroImg} alt={name ?? "Item"} className="w-full h-full object-contain" loading="lazy" />;
  }
  if (url) return <img src={url} alt={name ?? "Item"} className="w-full h-full object-contain" loading="lazy" />;
  return <span className="text-2xl">🎁</span>;
}


// ============ Store (shop) dialog with CRUD + purchase ============
function StoreDialog({
  open, onOpenChange, tcId, studentId, studentName, balance, onPurchased,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tcId: string;
  studentId: string;
  studentName: string;
  balance: number;
  onPurchased: () => void;
}) {
  const listItems = useServerFn(listShopItems);
  const upsert = useServerFn(upsertShopItem);
  const del = useServerFn(deleteShopItem);
  const buy = useServerFn(purchaseShopItem);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await listItems({ data: { tcId } });
      setItems(r.items ?? []);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao carregar loja");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) reload(); /* eslint-disable-next-line */ }, [open]);

  const doBuy = async (item: any) => {
    if (balance < item.cost_futbux) { toast.error("Saldo insuficiente."); return; }
    if (!confirm(`Adquirir "${item.name}" por ${item.cost_futbux} FUTBUX?`)) return;
    setBusyId(item.id);
    try {
      await buy({ data: { studentId, itemId: item.id } });
      toast.success("Item adquirido!");
      onPurchased();
    } catch (e: any) {
      toast.error(e?.message || "Erro na compra");
    } finally { setBusyId(null); }
  };

  const doDelete = async (item: any) => {
    if (!confirm(`Excluir "${item.name}" da loja?`)) return;
    try {
      await del({ data: { id: item.id, tcId } });
      toast.success("Item excluído");
      await reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao excluir");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Loja de Itens
          </DialogTitle>
          <DialogDescription>
            Compre itens com FUTBUX para {studentName}. Saldo atual:
            <span className="ml-1 inline-flex items-center gap-1 font-bold text-amber-800">
              <img src={futbuxCoin} alt="" className="w-4 h-4" /> {balance.toLocaleString("pt-BR")}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => { setEditing(null); setShowForm(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Cadastrar item
          </Button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-muted-foreground">Carregando…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">Nenhum item cadastrado ainda.</div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-auto">
            {items.map((it) => (
              <Card key={it.id} className={`p-3 ${!it.active ? "opacity-60" : ""}`}>
                <div className="w-full aspect-square rounded-md bg-muted/40 flex items-center justify-center mb-2 overflow-hidden">
                  <ItemImage url={it.image_url} name={it.name} />
                </div>
                <div className="font-semibold text-sm truncate">{it.name}</div>
                {it.description && <div className="text-xs text-muted-foreground line-clamp-2">{it.description}</div>}
                <div className="flex items-center gap-1 mt-1 font-bold text-amber-800">
                  <img src={futbuxCoin} alt="" className="w-4 h-4" />
                  {it.cost_futbux.toLocaleString("pt-BR")}
                </div>
                <div className="flex gap-1 mt-2">
                  <Button
                    size="sm"
                    className="flex-1 bg-sky-500 hover:bg-sky-600 text-white"
                    disabled={busyId === it.id || !it.active || balance < it.cost_futbux}
                    onClick={() => doBuy(it)}
                  >
                    {busyId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Comprar"}
                  </Button>
                  <Button size="icon" variant="outline" onClick={() => { setEditing(it); setShowForm(true); }} title="Editar">
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="icon" variant="outline" onClick={() => doDelete(it)} title="Excluir">
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <ShopItemForm
          open={showForm}
          onOpenChange={setShowForm}
          tcId={tcId}
          item={editing}
          onSaved={async () => { setShowForm(false); await reload(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function ShopItemForm({
  open, onOpenChange, tcId, item, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tcId: string;
  item: any | null;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertShopItem);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image_url, setImageUrl] = useState("");
  const [cost, setCost] = useState<string>("0");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(item?.name ?? "");
      setDescription(item?.description ?? "");
      setImageUrl(item?.image_url ?? "");
      setCost(String(item?.cost_futbux ?? 0));
      setActive(item?.active ?? true);
    }
  }, [open, item]);

  const save = async () => {
    if (!name.trim()) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    try {
      await upsert({
        data: {
          id: item?.id ?? undefined,
          tcId,
          name: name.trim(),
          description: description || null,
          image_url: image_url || null,
          cost_futbux: Number(cost) || 0,
          active,
        },
      });
      toast.success("Item salvo");
      onSaved();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{item ? "Editar item" : "Cadastrar item"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Nome *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Descrição</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1">
            <Label>URL da imagem (opcional)</Label>
            <Input value={image_url} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
          </div>
          <div className="space-y-1">
            <Label>Custo em FUTBUX</Label>
            <Input type="number" min={0} value={cost} onChange={(e) => setCost(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={active} onCheckedChange={setActive} />
            <Label>Ativo na loja</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StudentPhoto({ tcId, value, onChange, label }: { tcId: string; value: string | null; onChange: (p: string | null) => void; label: string; }) {
  const signedUrl = useSignedUrl(value);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const url = localUrl ?? signedUrl;
  const [busy, setBusy] = useState(false);
  const [rotating, setRotating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setLocalUrl(null); }, [value]);

  const rotate = async () => {
    if (!value || !url) return;
    setRotating(true);
    try {
      const rotatedBlob = await rotateImageFromUrl(url);
      const { error } = await supabase.storage
        .from("student_photos")
        .upload(value, rotatedBlob, { contentType: "image/jpeg", upsert: true });
      if (error) throw error;
      setLocalUrl(URL.createObjectURL(rotatedBlob));
    } catch (e: any) {
      toast.error(e?.message || "Erro ao girar imagem");
    } finally {
      setRotating(false);
    }
  };

  const upload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) { toast.error("Arquivo muito grande (máx 5MB)"); return; }
    setBusy(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) { toast.error("Sessão expirada"); return; }
      const path = `${tcId}/u/${uid}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("student_photos").upload(path, file, { contentType: file.type });
      if (error) throw error;
      if (value) await supabase.storage.from("student_photos").remove([value]);
      onChange(path);
    } catch (e: any) {
      toast.error(e?.message || "Erro no upload");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {url ? (
        <div className="relative h-28 w-28 rounded-full overflow-hidden border">
          <img src={url} alt={label} className="w-full h-full object-cover" />
          <button type="button" onClick={rotate} disabled={rotating} title="Girar foto" className="absolute bottom-0 left-0 p-1 bg-background/80 text-foreground rounded-tr">
            {rotating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
          </button>
          <button type="button" onClick={() => onChange(null)} className="absolute top-0 right-0 p-1 bg-destructive text-destructive-foreground rounded-bl">
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex flex-col items-center justify-center h-28 w-28 rounded-full border-2 border-dashed bg-muted/20 hover:bg-muted/40"
          disabled={busy}
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>
            <Upload className="w-5 h-5 mb-1" />
            <span className="text-[10px] text-center px-1">{label}</span>
          </>)}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
    </div>
  );
}

function DocPhoto({ tcId, value, onChange, label }: { tcId: string; value: string | null; onChange: (p: string | null) => void; label: string; }) {
  const signedUrl = useSignedUrl(value);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const url = localUrl ?? signedUrl;
  const [busy, setBusy] = useState(false);
  const [rotating, setRotating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setLocalUrl(null); }, [value]);

  const rotate = async () => {
    if (!value || !url) return;
    setRotating(true);
    try {
      const rotatedBlob = await rotateImageFromUrl(url);
      const { error } = await supabase.storage
        .from("student_photos")
        .upload(value, rotatedBlob, { contentType: "image/jpeg", upsert: true });
      if (error) throw error;
      setLocalUrl(URL.createObjectURL(rotatedBlob));
    } catch (e: any) {
      toast.error(e?.message || "Erro ao girar imagem");
    } finally {
      setRotating(false);
    }
  };

  const upload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) { toast.error("Arquivo muito grande (máx 5MB)"); return; }
    setBusy(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) { toast.error("Sessão expirada"); return; }
      const path = `${tcId}/u/${uid}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("student_photos").upload(path, file, { contentType: file.type });
      if (error) throw error;
      if (value) await supabase.storage.from("student_photos").remove([value]);
      onChange(path);
    } catch (e: any) {
      toast.error(e?.message || "Erro no upload");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {url ? (
        <div className="relative h-28 w-full rounded-lg overflow-hidden border">
          <img src={url} alt={label} className="w-full h-full object-cover" />
          <button type="button" onClick={rotate} disabled={rotating} title="Girar foto" className="absolute top-1 left-1 p-1 bg-background/80 text-foreground rounded">
            {rotating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
          </button>
          <button type="button" onClick={() => onChange(null)} className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded">
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex flex-col items-center justify-center h-28 w-full rounded-lg border-2 border-dashed border-green-500 bg-green-50 hover:bg-green-100"
          disabled={busy}
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin text-green-700" /> : (<>
            <Upload className="w-5 h-5 mb-1 text-green-700" />
            <span className="text-xs text-green-800 font-medium">{label}</span>
          </>)}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
    </div>
  );
}

type OcrResult = {
  name: string | null;
  cpf: string | null;
  birth_date: string | null;
  photo_box?: { x: number; y: number; w: number; h: number } | null;
  photo_path_index?: number | null;
};

function OcrButton({ paths, onResult }: { paths: string[]; onResult: (r: OcrResult) => void | Promise<void>; }) {

  const [busy, setBusy] = useState(false);
  const extract = useServerFn(extractDocument);
  return (
    <Button
      onClick={async () => {
        if (paths.length === 0) { toast.error("Envie pelo menos a frente do documento."); return; }
        setBusy(true);
        try {
          const r = await extract({ data: { paths } });
          await onResult(r);
        } catch (e: any) {
          toast.error(e?.message || "Erro na leitura");
        } finally { setBusy(false); }
      }}
      disabled={busy}
      className="bg-green-600 hover:bg-green-700 text-white w-full"
    >
      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
      Extrair dados do documento
    </Button>
  );
}
